import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { DataFlowBg } from "@/components/DataFlowBg";
import { CONTACT } from "@/constants/testIds";
import { EnvelopeSimple, MapPin, Buildings, ArrowRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, name, type = "text", required, value, onChange, error, testId, textarea = false }) => (
  <div>
    <label className="section-eyebrow block mb-2 text-[10px]">{label}{required && " *"}</label>
    {textarea ? (
      <textarea
        name={name}
        rows={5}
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="w-full bg-[#17223a] border border-white/10 focus:border-[#0089C8] outline-none px-4 py-3 text-sm text-white placeholder:text-slate-600 transition-colors"
      />
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="w-full bg-[#17223a] border border-white/10 focus:border-[#0089C8] outline-none px-4 py-3 text-sm text-white placeholder:text-slate-600 transition-colors"
      />
    )}
    {error && <div className="mt-1 text-[11px] text-[#FF6B35]">{error}</div>}
  </div>
);

const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", organization: "", country: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const handle = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const validate = () => {
    const e = {};
    ["name", "organization", "country", "email", "message"].forEach((k) => {
      if (!form[k] || String(form[k]).trim().length < 1) e[k] = t("contact.form.required");
    });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t("contact.form.required");
    if (form.message && form.message.trim().length < 5) e.message = t("contact.form.required");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t("contact.form.successTitle"), { description: t("contact.form.successDesc") });
      setForm({ name: "", organization: "", country: "", email: "", message: "" });
    } catch (err) {
      toast.error(t("contact.form.errorTitle"), { description: t("contact.form.errorDesc") });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
        <DataFlowBg density={5} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="section-eyebrow">{t("contact.eyebrow")}</div>
          <h1 className="h-display mt-6 text-4xl sm:text-5xl lg:text-6xl text-white max-w-4xl">
            {t("contact.title")}
          </h1>
          <p className="mt-6 max-w-2xl text-slate-300 text-base sm:text-lg leading-relaxed">
            {t("contact.subtitle")}
          </p>
        </div>
      </section>

      <section className="relative py-8 md:py-12 border-t border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
          <form
            onSubmit={submit}
            className="lg:col-span-7 sharp-card p-8 md:p-10 space-y-6"
            data-testid={CONTACT.form}
            noValidate
          >
            <div className="grid md:grid-cols-2 gap-5">
              <Field label={t("contact.form.name")} name="name" required value={form.name} onChange={handle} error={errors.name} testId={CONTACT.name} />
              <Field label={t("contact.form.organization")} name="organization" required value={form.organization} onChange={handle} error={errors.organization} testId={CONTACT.organization} />
              <Field label={t("contact.form.country")} name="country" required value={form.country} onChange={handle} error={errors.country} testId={CONTACT.country} />
              <Field label={t("contact.form.email")} name="email" type="email" required value={form.email} onChange={handle} error={errors.email} testId={CONTACT.email} />
            </div>
            <Field label={t("contact.form.message")} name="message" required value={form.message} onChange={handle} error={errors.message} testId={CONTACT.message} textarea />

            <div className="pt-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                management@cidesinvest.io
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid={CONTACT.submit}
                className="group inline-flex items-center gap-3 bg-[#FF6B35] hover:bg-[#E55A2B] disabled:opacity-70 text-white px-6 py-3.5 text-xs uppercase tracking-[0.24em] font-semibold transition-colors"
              >
                {sending ? t("contact.form.sending") : t("contact.form.submit")}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </form>

          <aside className="lg:col-span-5 space-y-4">
            <div className="section-eyebrow mb-1">{t("contact.coordinatesTitle")}</div>

            <div className="sharp-card p-6">
              <div className="flex items-start gap-4">
                <EnvelopeSimple size={20} className="text-[#0089C8] mt-0.5" weight="duotone" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400">{t("contact.email")}</div>
                  <a href="mailto:management@cidesinvest.io" className="text-white text-sm hover:text-[#0089C8] transition-colors" data-testid="contact-email-link">
                    management@cidesinvest.io
                  </a>
                </div>
              </div>
            </div>

            <div className="sharp-card p-6">
              <div className="flex items-start gap-4">
                <Buildings size={20} className="text-[#0089C8] mt-0.5" weight="duotone" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400">{t("contact.hq")}</div>
                  <div className="text-white text-sm">CIDES Invest and Trade Ltd</div>
                </div>
              </div>
              <div className="flex items-start gap-4 mt-4">
                <MapPin size={20} className="text-[#0089C8] mt-0.5" weight="duotone" />
                <div className="text-slate-300 text-sm leading-relaxed">
                  23 Woodvale Avenue<br />
                  Giffnock, Glasgow<br />
                  United Kingdom, G46 6RG
                </div>
              </div>
            </div>

            <div className="sharp-card p-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 mb-2">Legal</div>
              <div className="text-white text-sm">CIDES Invest and Trade Limited</div>
              <div className="font-mono text-[#0089C8] text-xs mt-1">Company n° SC564447</div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};

export default Contact;
